<template>
    <div class="card-wrapper" :class="[cardSize, { 'meteor-root': card.id === 'meteor' }]">
        <div class="close-icon-wrapper">
            <!-- 只有「被點開放大」時才顯示關閉鈕。微電網頁的能流圖本來就以 large
                 尺寸鋪在列上，用 cardSize 判斷會冒出一個關不掉任何東西的叉叉 -->
            <img
                v-if="isZoomed && card.id !== 'meteor'"
                src="@/assets/close.svg"
                alt="close"
                class="close-icon"
                @click="setLargeCardId('None')"
            />
        </div>

        <div class="weather-card" @click="setLargeCardId(card.id)">
            <div class="title">
                {{ $t(`card-name.${card.id}`) }}
            </div>
            <div class="data-wrapper">
                <div class="data" v-for="data in visibleData" :key="data.id">
                    {{ $t(data.text) }}　

                    <span v-if="data.id === 'WindDir'">
                        {{ translatedWindDirection }}
                    </span>
                    <span v-else-if="data.id === 'GPSLongitude'">
                        {{ getWGS84Longitude() }}
                    </span>
                    <span v-else-if="data.id === 'GPSLatitude'">
                        {{ getWGS84Latitude() }}
                    </span>
                    <span v-else>
                        <!-- 空值渲染成空字串會只剩下一個單位掛在那裡，看起來像版面壞了 -->
                        {{ getSelectedData(data.id) ?? '—' }}
                    </span>

                    {{ data.id === 'GPSLongitude' || data.id === 'GPSLatitude' ? '°' : data.unit }}
                </div>
            </div>
            <!-- 流星輪播：自行取圖，不依賴 historyFetched -->
            <div v-if="card.id === 'meteor'" class="graph meteor-graph">
                <div
                    v-if="cardSize === 'large'"
                    class="meteor-close-btn"
                    @click.stop="setLargeCardId('None')"
                >
                    <img src="@/assets/close.svg" alt="close" class="meteor-close-icon" />
                </div>
                <MeteorCarousel :cardSize="cardSize" />
            </div>

            <!-- 其餘卡片：等 historyFetched 才渲染圖表 -->
            <div v-else-if="historyFetched" class="graph" @click="setLargeCardId(card.id)">
                <WindChart v-if="card && card.id === 'wind'" :direction="translatedWindDirection" />
                <!-- 水質卡：上半水杯看當下等級，下半長條看今日走勢，
                     與空品兩張卡同一種版面。改用指針儀表的話補上 card.gauge 即可 -->
                <div
                    v-else-if="card.id === 'water' && !card.gauge"
                    class="gauge-trend-wrapper"
                >
                    <div class="gauge-part" :style="gaugePartStyle(WATER_IMAGE_ASPECT)">
                        <WaterChart :turbidity="getSelectedData('Turbidity')" />
                    </div>
                    <div v-if="cardSize !== 'small'" class="trend-part">
                        <ChartComponent
                            :chartType="card.chartType"
                            :options="options"
                            :datasets="datasets"
                            :labels="labels"
                        />
                    </div>
                </div>
                <GPSChart v-else-if="card.id === 'gps'" :options="options" :gpsData="datasets" />
                <!-- 環境品質卡：上半儀表看當下落在哪一級，下半長條看今日走勢。
                     這兩件事使用者是一起問的（現在髒不髒、剛剛有沒有更髒），
                     所以不採上面那種放大才換圖的做法，兩塊同時顯示 -->
                <div v-else-if="card.gauge && card.gauge.withChart" class="gauge-trend-wrapper">
                    <div class="gauge-part" :style="gaugePartStyle(BAND_ASPECT)">
                        <GaugeChart
                            :cardSize="cardSize"
                            :value="getSelectedData(card.gauge.dataId)"
                            :min="card.gauge.min"
                            :max="card.gauge.max"
                            :unit="card.gauge.unit"
                            :bands="gaugeBands"
                        />
                    </div>
                    <div v-if="cardSize !== 'small'" class="trend-part">
                        <ChartComponent
                            :chartType="card.chartType"
                            :options="options"
                            :datasets="datasets"
                            :labels="labels"
                        />
                    </div>
                </div>
                <!-- 儀表卡：未放大時看即時值，點開放大改看歷史折線 -->
                <LiquidGaugeChart
                    v-else-if="card.gauge && !isZoomed && card.gauge.type === 'liquid'"
                    :cardSize="cardSize"
                    :value="getSelectedData(card.gauge.dataId)"
                    :min="card.gauge.min"
                    :max="card.gauge.max"
                    :unit="card.gauge.unit"
                />
                <GaugeChart
                    v-else-if="card.gauge && !isZoomed"
                    :cardSize="cardSize"
                    :value="getSelectedData(card.gauge.dataId)"
                    :min="card.gauge.min"
                    :max="card.gauge.max"
                    :unit="card.gauge.unit"
                />
                <!-- 能流圖：放大時下方再疊三條功率折線，共用時間軸 -->
                <div
                    v-else-if="card.chartType === 'energyflow'"
                    class="energy-flow-wrapper"
                    :class="{ zoomed: isZoomed }"
                >
                    <div class="energy-flow-diagram">
                        <EnergyFlowChart
                            :cardSize="cardSize"
                            :pv="getSelectedData('pvpower')"
                            :grid="getSelectedData('gridpower')"
                            :load="getSelectedData('loadmeteractivepower')"
                            :soc="getSelectedData('batterysoc')"
                            :charging="getSelectedData('chargingpower')"
                            :discharging="getSelectedData('dischargingpower')"
                            :offline="microgridOffline"
                            :lastOnline="microgridLastOnline"
                        />
                    </div>
                    <div v-if="isZoomed" class="energy-flow-lines">
                        <ChartComponent
                            chartType="line"
                            :options="options"
                            :datasets="datasets"
                            :labels="labels"
                        />
                    </div>
                </div>
                <!-- 減碳成效：兩個換算值都是敘述性文字，沒有可畫的時間序列 -->
                <CarbonImpactChart
                    v-else-if="card.chartType === 'carbon'"
                    :cardSize="isZoomed ? 'large' : cardSize"
                    :carbon="getSelectedData('CarbonReduced')"
                    :area="getSelectedData('BambooArea')"
                />
                <ChartComponent
                    v-else
                    :chartType="card.chartType"
                    :options="options"
                    :datasets="datasets"
                    :labels="labels"
                />
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex'
import ChartComponent from '@/components/common/chart/ChartComponent.vue'
import WindChart from '@/components/common/chart/WindChart.vue'
import WaterChart from '@/components/common/chart/WaterChart.vue'
import GPSChart from '@/components/common/chart/GPSChart.vue'
import EnergyFlowChart from '@/components/common/chart/EnergyFlowChart.vue'
import CarbonImpactChart from '@/components/common/chart/CarbonImpactChart.vue'
import GaugeChart from '@/components/common/chart/GaugeChart.vue'
import { QUALITY_BANDS, BAND_VIEWBOX } from '@/components/common/chart/gaugeBands'
import LiquidGaugeChart from '@/components/common/chart/LiquidGaugeChart.vue'
import MeteorCarousel from '@/components/common/observation/MeteorCarousel.vue'
import proj4 from 'proj4'

// 定義 TWD97 轉 WGS84 參數 (台灣二度分帶 EPSG:3826 轉 世界大地測量系統 EPSG:4326)
const TWD97 =
    '+proj=tmerc +lat_0=0 +lon_0=121 +k=0.9999 +x_0=250000 +y_0=0 +ellps=GRS80 +units=m +no_defs'
const WGS84 = 'EPSG:4326'

export default {
    name: 'WeatherCard',
    components: {
        ChartComponent,
        WindChart,
        GPSChart,
        WaterChart,
        EnergyFlowChart,
        CarbonImpactChart,
        GaugeChart,
        LiquidGaugeChart,
        MeteorCarousel,
    },
    props: {
        cardId: {
            type: String,
            required: true,
        },
        cardSize: {
            type: String,
            required: true,
        },
    },
    computed: {
        ...mapState(['historyFetched', 'largeCardId']),
        ...mapGetters([
            'getCard',
            'getLatestData',
            'getChartTimeSeries',
            'getDatasets',
            'getSelectedData',
            'microgridOffline',
            'microgridLastOnline',
        ]),
        card() {
            return this.getCard(this.cardId)
        },
        // 卡片設定只寫分級表的名稱（如 'pm25'），避免 store 直接相依於配色檔
        gaugeBands() {
            const name = this.card.gauge && this.card.gauge.bands
            return name ? QUALITY_BANDS[name] : null
        },
        BAND_ASPECT() {
            return BAND_VIEWBOX.width / BAND_VIEWBOX.height
        },
        // 水質卡的水杯圖 assets/turbidity/*.png 是 400×200
        WATER_IMAGE_ASPECT() {
            return 2
        },
        // 能流圖在微電網頁本來就以 large 尺寸鋪滿一列，因此不能用 cardSize 判斷是否被放大，
        // 要看 largeCardId 才知道使用者是否真的點開了它
        isZoomed() {
            return this.largeCardId === this.card.id
        },
        // hidden 的欄位只餵給圖表元件，不佔卡片標頭版面（例如充放電功率）
        visibleData() {
            return (this.card.data || []).filter((data) => !data.hidden)
        },
        latestCardData() {
            return this.getLatestData(this.cardId)
        },
        labels() {
            return this.getChartTimeSeries()
        },
        options() {
            const options =
                this.card.chartOptions && JSON.parse(JSON.stringify(this.card.chartOptions)) // deep copy
            if (options?.scales) {
                Object.values(options.scales).forEach((option) => {
                    if (option.title) {
                        option.title.text = this.$t(option.title.text)
                    }
                })
            }
            return options
        },
        datasets() {
            return this.getDatasets(this.cardId).map((data) => {
                if (data.label) {
                    return {
                        ...data,
                        label: this.$t(data.label),
                    }
                }
                return data
            })
        },
        translatedWindDirection() {
            const windDirCode = this.getSelectedData('WindDir') || 'N'
            return this.$t(`wind-direction.${windDirCode}`)
        },
    },
    methods: {
        ...mapMutations(['setLargeCardId']),

        // 放大時左欄寬度 = 高度 × 圖的寬高比，圖才會上下撐滿、跟右邊的趨勢圖同高
        gaugePartStyle(aspect) {
            return this.isZoomed ? { '--gauge-aspect': aspect } : null
        },

        // 🌟 取得轉換後的經度 (Longitude)
        getWGS84Longitude() {
            // 注意：API 傳來的 GPSLatitude 其實裝的是 X(東西向)，GPSLongitude 裝的是 Y(南北向)
            const twd97_X = this.getSelectedData('GPSLatitude')
            const twd97_Y = this.getSelectedData('GPSLongitude')

            if (!twd97_X || !twd97_Y) return 0
            const [lon, lat] = proj4(TWD97, WGS84, [twd97_X, twd97_Y])
            return lon.toFixed(5)
        },

        // 🌟 取得轉換後的緯度 (Latitude)
        getWGS84Latitude() {
            // 同樣的交叉取值
            const twd97_X = this.getSelectedData('GPSLatitude')
            const twd97_Y = this.getSelectedData('GPSLongitude')

            if (!twd97_X || !twd97_Y) return 0
            const [lon, lat] = proj4(TWD97, WGS84, [twd97_X, twd97_Y])
            return lat.toFixed(5)
        },
    },
}
</script>

<style lang="scss" scoped>
/* 這裡保留你原本 WeatherCard 的 SCSS，包含 .small, .medium, .large 的排版 */
.card-wrapper {
    &.small {
        height: 100%;
        width: 22%;
    }
    &.medium {
        height: 100%;
        width: 45%;
    }
    &.large {
        height: 100%;
        width: 100%;
    }
    &.mobile {
        height: 100%;
        width: 100%;
    }
}

.close-icon-wrapper {
    position: absolute;
    top: 27vh;
    left: 93%;
}

.close-icon {
    cursor: pointer;
    position: relative;
    height: 2rem;
    align-self: self-end;
}
.weather-card {
    height: 100%;
    width: 100%;
    background-color: #fff;
    box-shadow: 5px 5px 5px 2px rgba(136, 136, 136, 0.2);
    border-radius: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
}
.small {
    .title {
        font-size: 1rem;
        font-weight: bold;
        padding: 0.5rem 1rem 0.5rem 1rem;
    }
    .data-wrapper {
        width: 100%;
        font-size: 0.85rem;
        display: flex;
        justify-content: space-evenly;
        flex-wrap: wrap;
        align-content: flex-start;
        /* 兩個數值擠在同一行時會黏在一起，補一個最小間距 */
        column-gap: 0.6rem;
    }
    .graph {
        padding: 0.25rem 0.5rem;
        height: 10%;
        width: 96%;
        flex-grow: 1;
        min-height: 0;
    }
}
.medium {
    .title {
        font-size: 1rem;
        font-weight: bold;
        padding: 0.5rem;
    }
    .data-wrapper {
        width: 100%;
        font-size: 0.85rem;
        display: flex;
        justify-content: space-around;
        flex-wrap: wrap;
    }
    .graph {
        padding: 0.25rem 0.5rem;
        height: 10%;
        width: 90%;
        flex-grow: 1;
        min-height: 0;
    }
}
.large {
    .title {
        width: 100%;
        font-size: 1.6rem;
        font-weight: bold;
        padding: 1.2rem 0;
    }
    .data-wrapper {
        width: 100%;
        display: flex;
        justify-content: space-around;
        flex-wrap: wrap;
    }
    .graph {
        padding: 1rem;
        height: 10%;
        width: 90%;
        flex-grow: 1;
        min-height: 0;
    }
    .meteor-graph {
        padding: 0.5rem 1rem 1.5rem;
        width: 100%;
        flex-grow: 1;
        min-height: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        overflow: hidden;

        /* 影像為 16:9，鎖定比例並置中，避免被裁切變形 */
        :deep(.carousel-container) {
            height: 100%;
            width: auto;
            aspect-ratio: 16 / 9;
            max-width: 100%;
        }
    }
}

/* 流星大圖：移除白卡外框，讓影像像獨立畫框浮在頁面背景上，
   左右不再是刺眼的白色空格，而是頁面紙張背景 */
.large.meteor-root {
    background-color: transparent;
    box-shadow: none;

    > .weather-card {
        background-color: transparent;
        box-shadow: none;
    }
    .title {
        padding: 0.6rem 0;
    }
    .meteor-graph {
        /* 縮到剛好包住 16:9 影像，關閉鈕才能釘在影像右上角 */
        position: relative;
        width: fit-content;
        max-width: 100%;
        padding: 0.25rem 0 1rem;
        overflow: visible;
    }
}

/* 關閉鈕直接釘在影像右上角，不論螢幕多寬都貼著圖 */
.meteor-close-btn {
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 5;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    cursor: pointer;
    background: rgba(0, 0, 0, 0.45);
    border-radius: 50%;
    backdrop-filter: blur(4px);
    transition: background 0.2s;
}
.meteor-close-btn:hover {
    background: rgba(0, 0, 0, 0.7);
}
.meteor-close-icon {
    height: 1.1rem;
    /* close.svg 為黑色，轉成白色才能在深色影像上清楚可見 */
    filter: brightness(0) invert(1);
}
.mobile {
    .title {
        font-size: 1.2rem;
        font-weight: bold;
        padding: 1rem 1rem 0.5rem 1rem;
    }
    .data-wrapper {
        width: 100%;
        display: flex;
        justify-content: space-around;
        flex-wrap: wrap;
    }
    .graph {
        padding: 0.5rem;
        height: 10rem;
        width: 90%;
        @media (orientation: landscape) {
            flex-grow: 1;
        }
    }
}

/* 能流圖：未放大時獨佔整個圖表區；放大後上下分割，下半給三條功率折線 */
/* 儀表在上、趨勢在下。比例偏向儀表，因為那是使用者第一眼要看的數字 */
.gauge-trend-wrapper {
    height: 100%;
    width: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;

    .gauge-part {
        flex: 1 1 58%;
        min-height: 0;
    }

    .trend-part {
        flex: 1 1 42%;
        min-height: 0;
    }
}

/* 點開放大後改左右並排。上下排時儀表只分到一半高度，在寬螢幕上等比縮放後
   反而比小卡還小；橫著排儀表就能吃滿整個卡片高度。走勢圖本來就是寬扁的，
   拿到右半邊反而更好讀。
   左欄不用固定比例：原本給 45% 寬，儀表畫布被寬度卡住，上下留白，跟右邊的
   趨勢圖對不齊（PM 09-14）。改成高度撐滿、寬度由圖的寬高比算出來，剩下的
   全給趨勢圖。60% 是保底，螢幕太窄時不讓趨勢圖被擠沒 */
.large .gauge-trend-wrapper {
    flex-direction: row;
    align-items: stretch;
    gap: 1.5rem;

    .gauge-part {
        flex: 0 0 auto;
        height: 100%;
        aspect-ratio: var(--gauge-aspect, 1.6);
        max-width: 60%;
        min-width: 0;
    }

    .trend-part {
        flex: 1 1 0;
        min-width: 0;
    }

    /* 水杯圖原本只縮不放，放大時要跟著撐滿左欄 */
    :deep(.cup-icon) {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }
}

.energy-flow-wrapper {
    height: 100%;
    width: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;

    .energy-flow-diagram {
        flex: 1 1 auto;
        min-height: 0;
    }

    &.zoomed {
        .energy-flow-diagram {
            flex: 1 1 55%;
        }
        .energy-flow-lines {
            flex: 1 1 45%;
            min-height: 0;
        }
    }
}

@media (max-width: 1280px) {
    .small .data-wrapper {
        font-size: 0.65rem;
    }
    .medium .data-wrapper {
        font-size: 0.8rem;
    }
}
</style>
